import type { ParkingListItem } from './parkingList';
import type { ParkingSpotPricing } from './parkingTypes';

export type ParkingPricingTemplate = {
  id: string;
  name: string | null;
  charging_period?: string | number | null;
  passenger_cars?: string | number | null;
  trucks?: string | number | null;
  buses?: string | number | null;
  moto_bikes?: string | number | null;
  campers?: string | number | null;
  charging_station?: string | number | null;
};

export type VehiclePricingCategory = 'passenger_cars' | 'trucks' | 'buses' | 'moto_bikes' | 'campers' | 'charging_station';

const normalizeLookupValue = (value: unknown): string | null => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length ? trimmed.toLowerCase() : null;
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }

  return null;
};

export const parsePriceNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const normalized = value.trim().replace(',', '.');
    if (!normalized.length) {
      return null;
    }

    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
};

const normalizePricingTemplate = (entry: unknown): ParkingPricingTemplate | null => {
  if (!entry || typeof entry !== 'object') {
    return null;
  }

  const record = entry as Record<string, unknown>;
  const id = normalizeLookupValue(record.id ?? record.price_id);
  if (!id) {
    return null;
  }

  return {
    id,
    name: typeof record.name === 'string' && record.name.trim().length > 0 ? record.name.trim() : null,
    charging_period: record.charging_period as string | number | null,
    passenger_cars: record.passenger_cars as string | number | null,
    trucks: record.trucks as string | number | null,
    buses: record.buses as string | number | null,
    moto_bikes: record.moto_bikes as string | number | null,
    campers: record.campers as string | number | null,
    charging_station: record.charging_station as string | number | null,
  };
};

const extractPricingArray = (payload: unknown, depth = 0): unknown[] | null => {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (!payload || typeof payload !== 'object' || depth > 4) {
    return null;
  }

  const record = payload as Record<string, unknown>;
  const candidates = [record['_data_'], record._data_, record.data, record.prices, record.price, record.list, record.results];

  for (const candidate of candidates) {
    if (!candidate || candidate === payload) {
      continue;
    }

    const extracted = extractPricingArray(candidate, depth + 1);
    if (extracted) {
      return extracted;
    }
  }

  return null;
};

export const normalizePricingTemplates = (payload: unknown): ParkingPricingTemplate[] => {
  const source = extractPricingArray(payload);
  if (!source) {
    return [];
  }

  return source.map((entry) => normalizePricingTemplate(entry)).filter((entry): entry is ParkingPricingTemplate => Boolean(entry));
};

export const templateToPricing = (template: ParkingPricingTemplate): ParkingSpotPricing => ({
  charging_period: template.charging_period == null ? undefined : String(template.charging_period),
  passenger_cars: parsePriceNumber(template.passenger_cars) ?? undefined,
  trucks: parsePriceNumber(template.trucks) ?? undefined,
  buses: parsePriceNumber(template.buses) ?? undefined,
  moto_bikes: parsePriceNumber(template.moto_bikes) ?? undefined,
  campers: parsePriceNumber(template.campers) ?? undefined,
  charging_station: parsePriceNumber(template.charging_station) ?? undefined,
});

export const findPricingTemplateForParking = (
  parking: Pick<ParkingListItem, 'id' | 'name' | 'priceTemplateId'> | null | undefined,
  templates: ParkingPricingTemplate[],
): ParkingPricingTemplate | null => {
  if (!parking || templates.length === 0) {
    return null;
  }

  const normalizedTemplateId = normalizeLookupValue(parking.priceTemplateId);
  if (normalizedTemplateId) {
    const byTemplateId = templates.find((template) => normalizeLookupValue(template.id) === normalizedTemplateId);
    if (byTemplateId) {
      return byTemplateId;
    }
  }

  const normalizedParkingId = normalizeLookupValue(parking.id);
  if (normalizedParkingId) {
    const byParkingId = templates.find((template) => normalizeLookupValue(template.id) === normalizedParkingId);
    if (byParkingId) {
      return byParkingId;
    }
  }

  const normalizedParkingName = normalizeLookupValue(parking.name);
  if (normalizedParkingName) {
    const byParkingName = templates.find((template) => normalizeLookupValue(template.name) === normalizedParkingName);
    if (byParkingName) {
      return byParkingName;
    }
  }

  return null;
};

export const resolveChargingPeriodMinutes = (pricing: ParkingSpotPricing | null | undefined): number | null => {
  const chargingPeriod = parsePriceNumber(pricing?.charging_period);
  if (chargingPeriod === null || chargingPeriod <= 0) {
    return null;
  }

  return chargingPeriod * 60;
};

export const resolveVehicleRateFromPricing = (
  pricing: ParkingSpotPricing | null | undefined,
  vehicleType: VehiclePricingCategory,
): number | null => {
  if (!pricing) {
    return null;
  }

  if (vehicleType === 'campers') {
    const campersRate = pricing.campers ?? pricing.kampery;
    return typeof campersRate === 'number' ? campersRate : null;
  }

  const rate = pricing[vehicleType];
  return typeof rate === 'number' ? rate : null;
};
